// purpose: sim が発火する全ゲームプレイ SE id を CombatAudioEngine のリッチキューへ振り分け、連射音だけをBGMグリッドへ接続する演出ディレクタ。?r2=1 経路専用。

import type { AudioEngine } from '../AudioEngine';
import type { CombatAudioEngine } from './CombatAudioEngine';

/**
 * レイヤ内部の数値（gain・filter・pan・秒オフセット）は CombatAudioEngine
 * リファレンスの記法にそろえてキュー内に直書きする。1 キュー = 1 音像であり、
 * 層ごとの値は互いの相対バランスとしてしか意味を持たないため、外に出すと
 * 「どの層のどの値か」が読めなくなる。外に出すのは *意味の単位*
 * （id・スロットル間隔・ルート音・段差）だけに限る。
 */

/** SE id（sim 側の発火点で確定済み。新規 id は追加しない）。 */
export const SFX_PICKUP = 0;
export const SFX_PLAYER_SHOT = 1;
export const SFX_SMALL_KILL = 2;
export const SFX_POWER_DOWN = 3;
export const SFX_LARGE_KILL = 4;
export const SFX_LASER = 5;
export const SFX_RICOCHET = 6;
export const SFX_TETHER = 7;
export const SFX_DEVICE_1 = 8;
export const SFX_DEVICE_2 = 9;
export const SFX_DEVICE_3 = 10;
export const SFX_DEVICE_4 = 11;
export const SFX_BOSS_SPAWN = 12;
export const SFX_BOSS_PHASE = 13;
export const SFX_BOSS_DEFEAT = 14;
export const SFX_PLAYER_DOWN = 15;
export const SFX_GATE_OPEN = 16;
export const SFX_PURCHASE_OK = 17;
export const SFX_PURCHASE_FAIL = 18;
export const SFX_WEAPON_LEVEL_UP = 19;

/** AudioContext が実際に発音できる状態。これ以外は旧 AudioEngine へ委ねる。 */
const RUNNING_STATE = 'running';
const SAME_GRID_EPSILON = .001;
/** 全武器の通常連射間隔を覆い、押し続けた列だけを同じburstとして扱う。 */
const PLAYER_SHOT_BURST_GAP_SECONDS = .42;
/** Web Audio schedulerが確実に予約でき、操作上の待ちには感じない最小先行時間。 */
const PLAYER_SHOT_GRID_LEAD_SECONDS = .012;
/** Grade V laserは短い更新列を1回の保持として扱い、16分ごとの走査音へ畳む。 */
const LINK_LASER_BURST_GAP_SECONDS = .24;
const LINK_LASER_GRID_LEAD_SECONDS = .012;
const LINK_LASER_FALLBACK_INTERVAL_SECONDS = .1;

/** リファレンス同様、スケジュールは現在時刻より僅かに先へ置く。 */
const CUE_LEAD = .003;

/** pickup は同 tick に複数発火しうる。この間隔未満は間引いて櫛状の飽和を防ぐ。 */
const PICKUP_MIN_INTERVAL = .04;
const PICKUP_ROOT_MIDI = 99;
/** 連続取得でルートを ±2 半音に振り、同じ音の反復に聞こえないようにする。 */
const PICKUP_ALT_OFFSETS: readonly number[] = [2, -2];

/** 装置系（tether + 4 デバイス）は同一テンプレートを id ごとの根音差で塗り分ける。 */
const DEVICE_ROOT_OFFSETS: ReadonlyMap<number, number> = new Map([
  [SFX_TETHER, 0],
  [SFX_DEVICE_1, 2],
  [SFX_DEVICE_2, 5],
  [SFX_DEVICE_3, 7],
  [SFX_DEVICE_4, 9],
]);
const DEVICE_CHARGE_MIDI = 45;
const DEVICE_PEAK_MIDI = 81;
const DEVICE_SUB_MIDI = 46;
const DEVICE_SUB_GLIDE_MIDI = 30;
/** チャージが着火に転じるまでの時間。charge の duration と一致させる。 */
const DEVICE_CHARGE_TIME = .12;

/** ボス警報のクラクソン。低い二和音を 3 回。 */
const BOSS_ALARM_ROOT_MIDI = 38;
const BOSS_ALARM_INTERVALS: readonly number[] = [0, 7];
const BOSS_ALARM_REPEATS = 3;
const BOSS_ALARM_SPACING = .22;

/** ゲート開放の穏やかな和音。 */
const GATE_ROOT_MIDI = 87;
const GATE_INTERVALS: readonly number[] = [0, 7, 12];

/** 購入失敗のブザー。短い矩形波を 2 連。 */
const PURCHASE_FAIL_MIDI = 43;
const PURCHASE_FAIL_REPEATS = 2;
const PURCHASE_FAIL_SPACING = .1;

/** 購入成功・武器強化は scorePop の段（3000 / 5000）に対応づける。 */
const PURCHASE_OK_POINTS = 3000;
const WEAPON_LEVEL_UP_POINTS = 5000;
const WEAPON_LEVEL_UP_DELAY = .05;

/* ─────────────── 補強レイヤ（round 2）───────────────
 * round 1 の「重さ」は sub 帯（MIDI 16-33 ≒ 20-55Hz）に置いた。ノート PC の
 * 内蔵スピーカーはこの帯域を再生できないため、重量増が体感ゼロになった。
 * ここでは *どのスピーカーでも鳴る* 中低域（MIDI 40-54 ≒ 80-190Hz）に胴を
 * 足して補強する。既存キューは差し替えず、後ろに重ねるだけ。
 * 値は耳で詰める前提。まず SHOT_BODY_GAIN（毎射・最多発火）から触ること。
 */

/** 胴レイヤの共通エンベロープ。立ち上がりは実質瞬時、減衰は尺なり。 */
const BODY_ATTACK = .002;
const BODY_RELEASE_RATIO = .8;
/** 胴は既存の kill/爆発層と同格で残す。射撃は完成済みsample一音なので補強対象外。 */
const BODY_PRIORITY_HEAVY = 94;

interface BodyLayer {
  /** キュー先頭からの秒オフセット。 */
  readonly delay: number;
  readonly midi: number;
  readonly glideTo: number;
  readonly gain: number;
  readonly duration: number;
  readonly pan: number;
}

/**
 * id ごとの胴スタック。ここが重量の正本。
 * 装置系（7-11）は着火が CUE_LEAD ではなくチャージ後のため表に載せず
 * deviceIgnition 内で直接鳴らす。表の id は throttle を持たないものだけ
 * （pickup のような間引きキューを載せると間引き時も胴だけ鳴ってしまう）。
 */
const BODY_LAYERS_BY_ID: ReadonlyMap<number, readonly BodyLayer[]> = new Map([
  [SFX_SMALL_KILL, [
    { delay: 0, midi: 50, glideTo: 41, gain: .3, duration: .16, pan: 0 },
  ]],
  [SFX_LARGE_KILL, [
    { delay: 0, midi: 48, glideTo: 38, gain: .34, duration: .24, pan: -.12 },
    { delay: .05, midi: 45, glideTo: 36, gain: .26, duration: .3, pan: .14 },
  ]],
  [SFX_BOSS_PHASE, [
    { delay: 0, midi: 50, glideTo: 40, gain: .34, duration: .3, pan: -.16 },
    { delay: .03, midi: 46, glideTo: 37, gain: .32, duration: .36, pan: .18 },
    { delay: .07, midi: 43, glideTo: 34, gain: .3, duration: .42, pan: 0 },
  ]],
  [SFX_BOSS_DEFEAT, [
    { delay: 0, midi: 48, glideTo: 38, gain: .38, duration: .4, pan: -.2 },
    { delay: .045, midi: 45, glideTo: 35, gain: .34, duration: .46, pan: .2 },
    { delay: .09, midi: 41, glideTo: 33, gain: .3, duration: .5, pan: 0 },
  ]],
  [SFX_PLAYER_DOWN, [
    { delay: 0, midi: 50, glideTo: 38, gain: .38, duration: .36, pan: -.22 },
    { delay: .035, midi: 46, glideTo: 36, gain: .33, duration: .44, pan: .22 },
    { delay: .08, midi: 43, glideTo: 34, gain: .3, duration: .5, pan: 0 },
  ]],
]);

/** 胴に噛ませる打点（アタックの輪郭）。低めの bright で「板を叩く」側に寄せる。 */
interface ClickLayer {
  readonly gain: number;
  readonly bright: number;
}
const CLICK_BY_ID: ReadonlyMap<number, ClickLayer> = new Map([
  [SFX_SMALL_KILL, { gain: .12, bright: 6500 }],
]);

/** 大型撃破だけに足す破砕ノイズ。胴の直後に瓦礫を落とす。 */
const CRUNCH_GAIN = .18;
const CRUNCH_DURATION = .2;
const CRUNCH_START_HZ = 1800;
const CRUNCH_END_HZ = 220;

/** 装置着火の胴（着火時刻に置くため表ではなく deviceIgnition 内で鳴らす）。 */
const DEVICE_BODY_MIDI = 45;
const DEVICE_BODY_GAIN = .24;
const DEVICE_BODY_DURATION = .2;

export interface LegacyDuckSpec {
  readonly target: number;
  readonly duration: number;
}

/**
 * リッチ側で鳴らした重い演出に合わせ、旧 AudioEngine が鳴らし続けている BGM
 * だけをダッキングするための表。SE 自体は旧経路では鳴らないため、旧 playSfx
 * 内の duck 相当をここで肩代わりする。
 */
export const LEGACY_DUCK_BY_SFX_ID: ReadonlyMap<number, LegacyDuckSpec> = new Map([
  // SEを主役化せず、BGMの床を保ったままイベントの呼吸だけを作る。
  [SFX_SMALL_KILL, { target: .82, duration: .075 }],
  [SFX_LARGE_KILL, { target: .68, duration: .13 }],
  [SFX_BOSS_SPAWN, { target: .48, duration: .7 }],
  [SFX_BOSS_PHASE, { target: .58, duration: .38 }],
  [SFX_BOSS_DEFEAT, { target: .3, duration: .95 }],
  [SFX_PLAYER_DOWN, { target: .42, duration: .7 }],
]);

export class GameplaySfxDirector {
  /** GameRuntime が id 単位で引くための公開ビュー（実体は module const・生成なし）。 */
  readonly duckLegacy: ReadonlyMap<number, LegacyDuckSpec> = LEGACY_DUCK_BY_SFX_ID;
  private lastPickupAt = Number.NEGATIVE_INFINITY;
  private lastPlayerShotAt = Number.NEGATIVE_INFINITY;
  private lastPlayerShotGrid = Number.NEGATIVE_INFINITY;
  private lastLinkLaserAt = Number.NEGATIVE_INFINITY;
  private lastLinkLaserGrid = Number.NEGATIVE_INFINITY;
  private lastLinkLaserFallbackAt = Number.NEGATIVE_INFINITY;
  private pickupAlt = 0;

  constructor(
    private readonly engine: CombatAudioEngine,
    private readonly musicClock: AudioEngine,
  ) {}

  /**
   * 単一の入口。engine が発音可能で id を担当できたときだけ true を返す。
   * false のとき呼び出し側は旧 AudioEngine.playSfx へフォールバックする。
   */
  play(id: number, chain: number, formation: number): boolean {
    const ctx = this.engine.ctx;
    if (!ctx || ctx.state !== RUNNING_STATE) return false;
    const handled = this.dispatch(ctx, id, chain, formation);
    if (handled) this.reinforce(ctx, id);
    return handled;
  }

  /** 本体キュー。担当外の id だけ false（pickup は間引き時も担当済み＝true）。 */
  private dispatch(ctx: AudioContext, id: number, chain: number, formation: number): boolean {
    const engine = this.engine;
    switch (id) {
      case SFX_PICKUP: this.pickup(ctx); return true;
      case SFX_PLAYER_SHOT: this.playerShot(ctx, formation); return true;
      case SFX_SMALL_KILL: engine.normalKill(chain); return true;
      case SFX_POWER_DOWN: this.powerDown(ctx); return true;
      case SFX_LARGE_KILL: engine.mediumExplosion(chain); return true;
      case SFX_LASER: this.laser(ctx); return true;
      case SFX_RICOCHET: this.ricochet(ctx); return true;
      case SFX_TETHER:
      case SFX_DEVICE_1:
      case SFX_DEVICE_2:
      case SFX_DEVICE_3:
      case SFX_DEVICE_4: this.deviceIgnition(ctx, id); return true;
      case SFX_BOSS_SPAWN: this.bossAlarm(ctx); return true;
      case SFX_BOSS_PHASE: engine.bossPartBreak(); return true;
      case SFX_BOSS_DEFEAT: engine.overkillExplosion(); return true;
      case SFX_PLAYER_DOWN: this.playerDown(ctx); return true;
      case SFX_GATE_OPEN: this.gateOpen(ctx); return true;
      case SFX_PURCHASE_OK: engine.scorePop(PURCHASE_OK_POINTS); return true;
      case SFX_PURCHASE_FAIL: this.purchaseFail(ctx); return true;
      case SFX_WEAPON_LEVEL_UP: engine.scorePop(WEAPON_LEVEL_UP_POINTS, WEAPON_LEVEL_UP_DELAY); return true;
      default: return false;
    }
  }

  /**
   * 押し始めの一発だけは即時。以降の連射は次の16分へ置き、同じグリッドへ
   * 入った複数のsim射撃を一音へ畳む。弾の生成時刻とDPSには触れない。
   */
  private playerShot(ctx: AudioContext, formation: number): void {
    const now = ctx.currentTime;
    const beginsBurst = now - this.lastPlayerShotAt > PLAYER_SHOT_BURST_GAP_SECONDS;
    this.lastPlayerShotAt = now;
    if (beginsBurst) {
      this.lastPlayerShotGrid = Number.NEGATIVE_INFINITY;
      this.engine.shot(undefined, formation);
      return;
    }

    const at = this.musicClock.nextR2MusicGridTime(1, PLAYER_SHOT_GRID_LEAD_SECONDS);
    if (at === null) {
      this.engine.shot(undefined, formation);
      return;
    }
    if (at <= this.lastPlayerShotGrid + SAME_GRID_EPSILON) return;
    this.lastPlayerShotGrid = at;
    this.engine.shot(at, formation);
  }

  /**
   * 可聴中低域（MIDI 40-54）の胴。sub 帯と違い内蔵スピーカーでも必ず鳴るため、
   * 「重い」の実体はここが担う。
   */
  private body(when: number, midi: number, gain: number, duration: number, glideTo = midi, pan = 0, priority = BODY_PRIORITY_HEAVY): void {
    this.engine.note({ midi, glideTo, duration, when, type: 'triangle', gain, attack: BODY_ATTACK, release: duration * BODY_RELEASE_RATIO, priority, bus: 'sfx', tag: 'reinforce:body', pan });
  }

  /** 本体キューの上に胴・打点・破砕を重ねる。表にない id は素通り。 */
  private reinforce(ctx: AudioContext, id: number): void {
    const layers = BODY_LAYERS_BY_ID.get(id);
    if (!layers) return;
    const engine = this.engine;
    const t = ctx.currentTime + CUE_LEAD;
    const priority = BODY_PRIORITY_HEAVY;
    for (let i = 0; i < layers.length; i += 1) {
      const layer = layers[i];
      if (!layer) continue;
      this.body(t + layer.delay, layer.midi, layer.gain, layer.duration, layer.glideTo, layer.pan, priority);
    }
    const click = CLICK_BY_ID.get(id);
    if (click) engine.transient({ when: t, gain: click.gain, priority: priority - 1, tag: 'reinforce:click', bright: click.bright, duration: .012 });
    if (id !== SFX_LARGE_KILL) return;
    engine.note({ type: 'noise', duration: CRUNCH_DURATION, when: t + .02, gain: CRUNCH_GAIN, attack: .002, release: CRUNCH_DURATION * .84, priority: priority - 2, bus: 'sfx', tag: 'reinforce:crunch', filter: { type: 'bandpass', start: CRUNCH_START_HZ, end: CRUNCH_END_HZ, q: 1.4 }, pan: .1 });
  }

  /** 0: 拾得。軽い一撃のベル + 極小のきらめき。間引いても「担当済み」として true を返す。 */
  private pickup(ctx: AudioContext): void {
    const now = ctx.currentTime;
    if (now - this.lastPickupAt < PICKUP_MIN_INTERVAL) return;
    this.lastPickupAt = now;
    const engine = this.engine;
    const t = now + CUE_LEAD;
    const d = engine.detune();
    const offset = PICKUP_ALT_OFFSETS[this.pickupAlt] ?? 0;
    this.pickupAlt = (this.pickupAlt + 1) % PICKUP_ALT_OFFSETS.length;
    engine.bell({ midi: PICKUP_ROOT_MIDI + offset, when: t, duration: .17, gain: .05, priority: 55, pan: .12, tag: 'pickup:bell', detune: d, reverb: .18 });
    engine.transient({ when: t, gain: .026, priority: 56, tag: 'pickup:glint', bright: 11800, duration: .011, pan: -.1 });
  }

  /** 3: プリズム停止。報酬ではなく萎み。下降サチュレーションと小さな底突き。 */
  private powerDown(ctx: AudioContext): void {
    const engine = this.engine;
    const t = ctx.currentTime + CUE_LEAD;
    const d = engine.detune();
    engine.note({ midi: 70, glideTo: 38, duration: .28, when: t, type: 'sawtooth', gain: .128, attack: .004, release: .21, priority: 68, bus: 'sfx', tag: 'prism:power-down', detune: d, filter: { start: 3400, end: 300, q: 1.7 }, pan: -.14, reverb: .16 });
    engine.note({ midi: 58, glideTo: 34, duration: .22, when: t + .012, type: 'triangle', gain: .075, attack: .003, release: .17, priority: 66, bus: 'sfx', tag: 'prism:power-down-body', detune: d, pan: .16 });
    engine.subImpact({ when: t + .016, midi: 40, glideTo: 26, duration: .16, gain: .152, priority: 65, tag: 'prism:power-down-tap' });
  }

  /** 5: 初回は即時点火、保持中は16分へ畳んだ上昇ノイズと細い鋸の走査パルス。 */
  private laser(ctx: AudioContext): void {
    const now = ctx.currentTime;
    const beginsBurst = now - this.lastLinkLaserAt > LINK_LASER_BURST_GAP_SECONDS;
    this.lastLinkLaserAt = now;
    if (beginsBurst) {
      this.lastLinkLaserGrid = Number.NEGATIVE_INFINITY;
      this.lastLinkLaserFallbackAt = now;
    }
    const gridAt = beginsBurst
      ? null
      : this.musicClock.nextR2MusicGridTime(1, LINK_LASER_GRID_LEAD_SECONDS);
    if (gridAt !== null && gridAt <= this.lastLinkLaserGrid + SAME_GRID_EPSILON) return;
    if (gridAt !== null) {
      this.lastLinkLaserGrid = gridAt;
    } else if (!beginsBurst) {
      if (now - this.lastLinkLaserFallbackAt < LINK_LASER_FALLBACK_INTERVAL_SECONDS) return;
      this.lastLinkLaserFallbackAt = now;
    }
    const engine = this.engine;
    const t = gridAt ?? now + CUE_LEAD;
    const d = engine.detune();
    engine.note({ type: 'noise', duration: .12, when: t, gain: .145, attack: .052, release: .062, priority: 80, bus: 'sfx', tag: 'laser:riser-air', detune: d, filter: { type: 'bandpass', start: 900, end: 7500, q: 3.4 }, pan: .22, reverb: .14 });
    engine.note({ midi: 60, glideTo: 84, duration: .12, when: t + .004, type: 'sawtooth', gain: .09, attack: .014, release: .07, priority: 79, bus: 'sfx', tag: 'laser:tone', detune: d, filter: { type: 'bandpass', start: 1200, end: 6400, q: 4.4 }, pan: -.18, echo: .1, reverb: .18 });
    engine.transient({ when: t + .102, gain: .08, priority: 78, tag: 'laser:ignite', bright: 10600, duration: .015, pan: .12 });
  }

  /** 6: 反射。硬い立ち上がりと下降する金属ピン。 */
  private ricochet(ctx: AudioContext): void {
    const engine = this.engine;
    const t = ctx.currentTime + CUE_LEAD;
    const d = engine.detune();
    engine.transient({ when: t, gain: .09, priority: 72, tag: 'reflect:snap', bright: 11600, duration: .012, pan: .16 });
    engine.note({ midi: 100, glideTo: 88, duration: .07, when: t + .002, type: 'sine', gain: .104, attack: .001, release: .055, priority: 71, bus: 'sfx', tag: 'reflect:ping', detune: d, filter: { type: 'bandpass', start: 6400, end: 3100, q: 5.2 }, pan: -.2, echo: .15, reverb: .2 });
    engine.note({ midi: 107, glideTo: 97, duration: .05, when: t + .006, type: 'square', gain: .035, attack: .001, release: .04, priority: 69, bus: 'sfx', tag: 'reflect:ping-partial', detune: d, pan: .28, echo: .15, reverb: .22 });
  }

  /** 7-11: 装置起動。チャージ → 着火 → 確定ベルの三段で「効いた」感を残す。 */
  private deviceIgnition(ctx: AudioContext, id: number): void {
    const engine = this.engine;
    const offset = DEVICE_ROOT_OFFSETS.get(id) ?? 0;
    const t = ctx.currentTime + CUE_LEAD;
    const hit = t + DEVICE_CHARGE_TIME;
    const d = engine.detune();
    engine.duckMusic(.52, .17, .004, .022);
    engine.note({ midi: DEVICE_CHARGE_MIDI + offset, glideTo: DEVICE_PEAK_MIDI + offset, duration: DEVICE_CHARGE_TIME, when: t, type: 'sine', gain: .186, attack: .086, release: .028, priority: 88, bus: 'sfx', tag: 'device:charge', detune: d, filter: { type: 'bandpass', start: 300, end: 5200, q: 6 }, pan: -.18, reverb: .16 });
    engine.transient({ when: hit, gain: .171, priority: 90, tag: 'device:ignite', bright: 10800, duration: .022 });
    engine.subImpact({ when: hit, midi: DEVICE_SUB_MIDI + offset, glideTo: DEVICE_SUB_GLIDE_MIDI + offset, duration: .22, gain: .283, priority: 90, tag: 'device:body' });
    // sub は小型スピーカーで消える。着火の重さは可聴中低域の胴で担保する。
    this.body(hit, DEVICE_BODY_MIDI + offset, DEVICE_BODY_GAIN, DEVICE_BODY_DURATION);
    engine.bell({ midi: DEVICE_PEAK_MIDI + offset, when: hit + .03, duration: .26, gain: .104, priority: 87, pan: .28, tag: 'device:confirm', detune: d, reverb: .32 });
  }

  /** 12: ボス出現。低いクラクソン 3 回 + 立ち上がる低域 + 空気。祝祭ではなく警報。 */
  private bossAlarm(ctx: AudioContext): void {
    const engine = this.engine;
    const t = ctx.currentTime + CUE_LEAD;
    const d = engine.detune();
    engine.duckMusic(.11, .84, .006, .13);
    for (let i = 0; i < BOSS_ALARM_REPEATS; i += 1) {
      const at = t + i * BOSS_ALARM_SPACING;
      BOSS_ALARM_INTERVALS.forEach((iv, j) => engine.note({ midi: BOSS_ALARM_ROOT_MIDI + iv, duration: .19, when: at + j * .004, type: 'sawtooth', gain: .152 / (1 + j * .28), attack: .009, release: .13, priority: 95 - j, bus: 'sfx', tag: 'boss:alarm', detune: d, pan: j ? .3 : -.3, filter: { type: 'bandpass', start: 300, end: 1200, q: 4.5 }, echo: .12, reverb: .26 }));
    }
    engine.subImpact({ when: t, midi: 29, glideTo: 38, duration: .62, gain: .29, priority: 93, tag: 'boss:alarm-swell' });
    engine.note({ type: 'noise', duration: .72, when: t + .05, gain: .102, attack: .24, release: .44, priority: 84, bus: 'sfx', tag: 'boss:alarm-air', filter: { type: 'highpass', start: 700, end: 5200, q: .6 }, pan: .22, reverb: .34 });
  }

  /** 15: 自機撃墜。重い爆発の身体だけを置く。旋律的な断絶は comboBreak が既に担う。 */
  private playerDown(ctx: AudioContext): void {
    const engine = this.engine;
    const t = ctx.currentTime + CUE_LEAD;
    const d = engine.detune();
    engine.duckMusic(.14, .78, .004, .11);
    engine.transient({ when: t, gain: .326, priority: 100, tag: 'player:down-snap-L', bright: 10200, duration: .03, pan: -.3 });
    engine.transient({ when: t + .012, gain: .247, priority: 99, tag: 'player:down-snap-R', bright: 8400, duration: .036, pan: .32 });
    engine.subImpact({ when: t + .004, midi: 38, glideTo: 16, duration: .6, gain: .551, priority: 100, tag: 'player:down-sub' });
    engine.note({ type: 'noise', duration: .58, when: t + .01, gain: .29, attack: .001, release: .48, priority: 97, bus: 'sfx', tag: 'player:down-blast', detune: d, filter: { start: 6800, end: 90, q: 1.2 }, reverb: .3 });
    engine.note({ midi: 72, glideTo: 48, duration: .34, when: t + .02, type: 'triangle', gain: .133, attack: .004, release: .27, priority: 90, bus: 'sfx', tag: 'player:down-fall', detune: d, filter: { type: 'lowpass', start: 2400, end: 260, q: 1.1 }, pan: -.16, reverb: .24 });
  }

  /** 16: 校正ゲート開放。遅いアタックの和音で「開いた」ことだけを静かに告げる。 */
  private gateOpen(ctx: AudioContext): void {
    const engine = this.engine;
    const t = ctx.currentTime + CUE_LEAD;
    const d = engine.detune();
    GATE_INTERVALS.forEach((iv, i) => engine.note({ midi: GATE_ROOT_MIDI + iv, duration: .62 + i * .05, when: t + i * .055, type: i % 2 ? 'sine' : 'triangle', gain: .061 / (1 + i * .12), attack: .046, release: .46, priority: 62 - i, bus: 'sfx', tag: 'gate:open', detune: d, pan: -.3 + i * .3, echo: .14, reverb: .5 }));
    engine.bell({ midi: GATE_ROOT_MIDI + 12, when: t + .16, duration: .42, gain: .049, priority: 59, pan: .22, tag: 'gate:open-shimmer', detune: d, reverb: .55 });
  }

  /** 18: 購入失敗。短い矩形ブザー 2 連。刺さらない程度に濁す。 */
  private purchaseFail(ctx: AudioContext): void {
    const engine = this.engine;
    const t = ctx.currentTime + CUE_LEAD;
    for (let i = 0; i < PURCHASE_FAIL_REPEATS; i += 1) {
      engine.note({ midi: PURCHASE_FAIL_MIDI, duration: .07, when: t + i * PURCHASE_FAIL_SPACING, type: 'square', gain: .102, attack: .002, release: .05, priority: 58, bus: 'sfx', tag: 'purchase:fail', filter: { type: 'bandpass', start: 500, end: 430, q: 2.2 }, pan: i ? .12 : -.12 });
    }
  }
}
